import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import { api } from '../../api/nocode';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';

const DEFAULT_CHECKOUT_URL = 'https://gospelwiseauthor.com/checkout/x6q07miyx1uiuoq';

function rowsFrom(response) {
  if (Array.isArray(response)) return response;
  return Array.isArray(response?.data) ? response.data : [];
}

function buildCheckoutUrl(baseUrl, params) {
  const fallback = DEFAULT_CHECKOUT_URL;
  const rawUrl = String(baseUrl || fallback).trim();

  try {
    const checkoutUrl = new URL(rawUrl);
    Object.entries(params).forEach(([key, value]) => {
      checkoutUrl.searchParams.set(key, value);
    });
    return checkoutUrl.toString();
  } catch {
    const separator = rawUrl.includes('?') ? '&' : '?';
    return `${rawUrl}${separator}${new URLSearchParams(params).toString()}`;
  }
}

export default function Upgrade() {
  const { user } = useAuth();
  const { currentRun } = useAcademy();
  const [checkoutLink, setCheckoutLink] = useState(DEFAULT_CHECKOUT_URL);

  useEffect(() => {
    let mounted = true;

    const loadCheckoutLink = async () => {
      try {
        const response = await api.getUnscoped('settings_gwaacademy');
        const settings = rowsFrom(response);
        const checkoutSetting = settings.find(
          (setting) => setting?.setting_key === 'global_all_access_link'
        );

        if (mounted && checkoutSetting?.value?.trim()) {
          setCheckoutLink(checkoutSetting.value.trim());
        }
      } catch (error) {
        console.error('Unable to load the All-Access checkout link', error);
      }
    };

    loadCheckoutLink();

    return () => {
      mounted = false;
    };
  }, []);

  const checkoutParams = {
    email: user?.email || '',
    run_id: currentRun?.id ? String(currentRun.id) : '',
    run_slug: currentRun?.slug || ''
  };
  const checkoutUrl = buildCheckoutUrl(checkoutLink, checkoutParams);
  
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold text-dark-green">Upgrade to All-Access</h1>
        <p className="text-lg text-muted max-w-2xl mx-auto">
          Get the complete GospelWise Author Academy experience, including lifetime replay access, personalized self-letters, and a dedicated lab partner.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 items-center">
        <Card className="bg-gradient-to-br from-gwa-blue to-blue-900 text-white border-none p-8 transform md:scale-105 shadow-xl">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold">All-Access Pass</h2>
              <p className="opacity-80">Early Bird Pricing</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">$9</div>
              <div className="text-sm opacity-80 line-through">$19 Regular</div>
            </div>
          </div>

          <ul className="space-y-4 mb-8">
            <li className="flex gap-3"><SafeIcon name="CheckCircle" className="text-amber shrink-0 mt-1" /> Full Compass Workbook Access</li>
            <li className="flex gap-3"><SafeIcon name="CheckCircle" className="text-amber shrink-0 mt-1" /> <b>Ongoing Replay Access</b> to all videos</li>
            <li className="flex gap-3"><SafeIcon name="CheckCircle" className="text-amber shrink-0 mt-1" /> <b>Self-Letter Studio</b> (Delivered via email)</li>
            <li className="flex gap-3"><SafeIcon name="CheckCircle" className="text-amber shrink-0 mt-1" /> <b>Lab Partner Pairing</b> for accountability</li>
            <li className="flex gap-3"><SafeIcon name="CheckCircle" className="text-amber shrink-0 mt-1" /> Permanent Compass archive download</li>
          </ul>

          <a href={checkoutUrl} className="block w-full">
            <Button variant="secondary" className="w-full text-lg py-3">
              Secure Your All-Access Pass
            </Button>
          </a>
          <p className="text-center text-xs opacity-70 mt-4">Price increases to $19 on July 13 at 1:30 PM EST</p>
        </Card>

        <div className="space-y-6">
          <h3 className="text-xl font-bold text-dark-green">Why Upgrade?</h3>
          <p className="text-muted text-sm leading-relaxed">
            The Free pass gives you a great taste of the academy, but the magic happens when you commit. The All-Access pass gives you the tools to actually finish what you start, with accountability built in.
          </p>
          <div className="bg-cream p-4 rounded-md border border-tan flex items-start gap-4">
            <SafeIcon name="Clock" className="text-amber text-2xl shrink-0" />
            <div>
              <h4 className="font-bold text-dark-green text-sm">Don't lose access!</h4>
              <p className="text-xs text-muted mt-1">Free users lose access to all academy materials on Saturday, July 19. All-Access users keep replays for ongoing access, plus permanent workbook downloads.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}